import { ApiError, type User } from "@acervo/api-client";

/** 3725 → "1h 02min"; 754 → "12min"; 40 → "40s". */
export function duration(totalS: number): string {
  const s = Math.max(0, Math.round(totalS));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}min`;
  if (m > 0) return `${m}min`;
  return `${s}s`;
}

/** 754 → "12:34"; 3725 → "1:02:05". */
export function clock(totalS: number): string {
  const s = Math.max(0, Math.floor(totalS));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(h ? 2 : 1, "0");
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function percent(pos: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((pos / total) * 100)));
}

const dtf = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});
export function dateTime(iso: string | null | undefined): string {
  return iso ? dtf.format(new Date(iso)) : "—";
}

const rtf = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });
export function relative(
  iso: string | null | undefined,
  now = Date.now(),
): string {
  if (!iso) return "nunca";
  const diff = (new Date(iso).getTime() - now) / 1000;
  const abs = Math.abs(diff);
  if (abs < 60) return "agora";
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), "day");
  return dateTime(iso);
}

export const roleLabel: Record<User["role"], string> = {
  owner: "Dono",
  admin: "Admin",
  author: "Autor",
  student: "Aluno",
  family: "Família",
};

export const visibilityLabel = {
  private: "Privado",
  unlisted: "Não listado",
  published: "Publicado",
} as const;

const messages: Record<string, string> = {
  invalid_credentials: "E-mail ou senha incorretos.",
  invalid_mfa_code:
    "Código inválido. Confira o app autenticador e tente de novo.",
  too_many_attempts:
    "Muitas tentativas. Aguarde alguns minutos e tente de novo.",
  account_blocked: "Esta conta está bloqueada. Fale com o administrador.",
  session_invalid: "Sua sessão terminou. Entre de novo.",
  email_taken: "Já existe uma conta com esse e-mail.",
  third_party_private: "Conteúdo de terceiros não pode deixar de ser privado.",
  forbidden: "Você não tem permissão para isso.",
  not_found: "Não encontrado.",
  storage_not_configured:
    "O armazenamento de vídeos ainda não foi configurado.",
};

/** Mensagem amigável para qualquer erro vindo da API ou da rede. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (messages[err.code]) return messages[err.code];
    if (err.status >= 500)
      return "O servidor teve um problema. Tente de novo em instantes.";
    return err.message.charAt(0).toUpperCase() + err.message.slice(1);
  }
  if (err instanceof TypeError) return "Sem conexão com o servidor.";
  return err instanceof Error ? err.message : "Algo deu errado.";
}
